import React, { useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export interface HelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  title: string;
  children: React.ReactNode;
}

/**
 * Reusable help sidebar panel matching the Dashboard help design:
 * floating FAB when collapsed, slide-up/panel when expanded with backdrop and Escape to close.
 */
export const HelpSidebar: React.FC<HelpSidebarProps> = ({
  isExpanded,
  onToggle,
  title,
  children,
}) => {
  useEffect(() => {
    if (!isExpanded) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onToggle();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded, onToggle]);

  return (
    <>
      {!isExpanded && (
        <IconButton
          onClick={onToggle}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            bgcolor: 'primary.main',
            color: 'white',
            width: 64,
            height: 64,
            boxShadow: 3,
            overflow: 'hidden',
            p: 0,
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: 6,
              transform: 'scale(1.05)',
              '& .help-badge': {
                opacity: 1,
                transform: 'translateX(4px) translateY(0)',
              },
            },
            transition: 'all 0.2s',
          }}
          aria-label={`Open ${title}`}
          title={title}
        >
          <Box
            component="img"
            src="/aardvark.svg"
            alt=""
            sx={{
              width: '85%',
              height: '85%',
              objectFit: 'contain',
              transform: 'translateX(-8%)',
            }}
          />
          <Box
            className="help-badge"
            sx={{
              position: 'absolute',
              bottom: 2,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              opacity: 0,
              transform: 'translateX(4px) translateY(10px)',
              transition: 'all 0.2s ease-out',
            }}
          >
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: '50%',
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            </Box>
          </Box>
        </IconButton>
      )}

      {isExpanded && (
        <>
          <Box
            onClick={onToggle}
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              transition: 'opacity 0.2s',
            }}
            aria-hidden="true"
          />
          <Box
            sx={{
              position: 'fixed',
              bottom: { xs: 0, sm: 24 },
              right: { xs: 0, sm: 24 },
              zIndex: 1301,
              bgcolor: 'background.paper',
              borderRadius: { xs: '16px 16px 0 0', sm: 2 },
              boxShadow: 24,
              width: { xs: '100%', sm: 400 },
              maxWidth: { xs: '100%', sm: '90vw' },
              maxHeight: { xs: '85vh', sm: '80vh' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                borderRadius: { xs: '16px 16px 0 0', sm: '8px 8px 0 0' },
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HelpOutlineIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  {title}
                </Typography>
              </Box>
              <IconButton
                onClick={onToggle}
                size="small"
                aria-label="Close help"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {children}
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default HelpSidebar;
